import React from "react";
import BaseModal from "../../../components/ui/BaseModal";
import BaseButton from "../../../components/ui/BaseButton";
import BaseTextField from "../../../components/ui/BaseTextField";
import BaseDatePicker from "../../../components/ui/BaseDatePicker";
import dayjs from "dayjs";
import * as yup from "yup";
import { useFormik } from "formik";
import { useMutation } from "@tanstack/react-query";
import {
  createEducation,
  updateUserEducation,
} from "../../../services/api/education";
import { useDispatch } from "react-redux";
import { showToast } from "../../../store/toast/toastSlice";
import BaseAutoSelect from "../../../components/ui/BaseAutoSelect";

const validationSchema = yup.object({
  institutionName: yup
    .string("Enter Institution Name")
    .required("Institution name is required"),
  educationType: yup
    .string("Enter Education Type")
    .required("Education type is required"),
  startDate: yup
    .date()
    .required("start date is required")
    .max(new Date(), "Start date can't be in the future"),

  endDate: yup
    .date()
    .required("end date is required")
    .min(yup.ref("startDate"), "End date can't be before start date"),
});

const AddEducationModal = ({
  open,
  close,
  refetchUserEducationList,
  setSelectedUserEducation,
  isEdit,
  selectedUserEducation,
  targetUserId,
}) => {
  const dispatch = useDispatch();
  const {
    isPending: isCreateEducationApiLoading,
    error: isEducationError,
    data: educationData,
    mutateAsync: createEducationMutation,
  } = useMutation({
    mutationFn: (payload) => createEducation(payload, targetUserId),
    enabled: false,
  });

  const {
    isPending: isUpdateEducationLoading,
    error: isUpdateEducationError,
    data: updatedEducationData,
    mutateAsync: updateEducationMutation,
  } = useMutation({
    mutationFn: updateUserEducation,
  });

  const formik = useFormik({
    initialValues: {
      institutionName: selectedUserEducation?.institutionName,
      educationType: selectedUserEducation?.educationType,
      startDate: selectedUserEducation?.startDate || null,
      endDate: selectedUserEducation?.endDate || null,
    },
    validationSchema: validationSchema,
    onSubmit: (values, { resetForm }) => {
      handleEducationSubmit();
    },
    validateOnChange: true,
    validateOnBlur: true,
    enableReinitialize: true,
  });

  const handleAddEducation = async () => {
    try {
      const educationResponse = await createEducationMutation(formik.values);
      dispatch(
        showToast({
          message: "Education added successfully",
          type: "success",
        })
      );
      formik.resetForm();
      close(false);
    } catch (error) {
      console.log(error);
      dispatch(
        showToast({
          message: error?.response?.data?.message,
          type: "error",
        })
      );
    }
  };

  const handleEditEducation = async () => {
    try {
      const educationResponse = await updateEducationMutation({
        ...formik.values,
        id: selectedUserEducation.id,
      });
      dispatch(
        showToast({
          message: "Education updated successfully",
          type: "success",
        })
      );
      formik.resetForm();
      close(false);
    } catch (error) {
      console.log(error);
      dispatch(
        showToast({
          message: error?.response?.data?.message,
          type: "error",
        })
      );
    }
  };

  const handleEducationSubmit = () => {
    if (isEdit) {
      handleEditEducation();
    } else {
      handleAddEducation();
    }
    refetchUserEducationList();
  };
  const parseDay = (v) => (v ? dayjs(v) : null);
  return (
    <BaseModal
      className="-top-[200px] overflow-auto"
      open={open}
      close={close}
    >
      <div className="py-7 px-5 sm:px-12 w-full lg:w-[900px] xl:w-[1000px]">
        <h6 className="text-dark font-normal border-b pb-1">Add Education</h6>

        <div className="mb-2 mt-3">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-x-14 gap-y-2 [&>*]:min-w-0">
            <BaseTextField
              isDefaultField={false}
              label="Institution Name"
              labelShrink={false}
              optional={false}
              id="institutionName"
              name="institutionName"
              value={formik.values.institutionName}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              error={
                formik.touched.institutionName &&
                Boolean(formik.errors.institutionName)
              }
              helperText={
                formik.touched.institutionName && formik.errors.institutionName
              }
            />
            <div>
              <BaseAutoSelect
                labelShrink={false}
                required={true}
                isDefaultField={false}
                fullHeight={false}
                fullWidth
                options={[
                  {
                    label: "Masters",
                    value: "MASTERS_DEGREE",
                  },
                  {
                    label: "Bachelor",
                    value: "BACHELORS_DEGREE",
                  },
                  {
                    label: "High School",
                    value: "HIGH_SCHOOL",
                  },
                  {
                    label: "Ceritification",
                    value: "CERTIFICATION",
                  },
                ]}
                label="Education Type"
                id="educationType"
                name="educationType"
                defaultValue={formik.values.educationType}
                value={formik.values.educationType}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                error={
                  formik.touched.educationType &&
                  Boolean(formik.errors.educationType)
                }
                helperText={
                  formik.touched.educationType && formik.errors.educationType
                }
                sx={{
                  "& .MuiInputBase-input": {
                    padding: "2px 5px",
                  },
                }}
              />
            </div>
            <div>
              <p className="font-normal">Start Date</p>
              <BaseDatePicker
                className="border-none"
                type="date"
                id="startDate"
                name="startDate"
                value={dayjs(formik.values.startDate)}
                maxDate={dayjs().endOf("day")}
                onChange={(event) => {
                  const selectedDate = dayjs(event)
                    .utcOffset(0, true)
                    .format("YYYY-MM-DDTHH:mm:ss.SSS[Z]");

                  formik.setFieldValue("startDate", selectedDate); // Store as string
                }}
                onBlur={formik.handleBlur}
                error={
                  formik.touched.startDate && Boolean(formik.errors.startDate)
                }
                helperText={formik.touched.startDate && formik.errors.startDate}
                slotProps={{
                  textField: {
                    sx: {
                      "& .MuiOutlinedInput-root": {
                        border: "none !important",
                        boxShadow: "1px 1px 4px 1px #00000021",
                        borderRadius: "5px",
                        backgroundColor: "white",
                        height: "48px",
                        display: "flex",
                        alignItems: "center",
                        margin: "8px 1.5px",
                      },
                      "& .MuiOutlinedInput-notchedOutline": {
                        border: "none !important",
                      },
                      "& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline":
                        {
                          border: "none !important",
                          boxShadow: "none",
                        },
                      "& .MuiInputBase-input": {
                        padding: "10px 14px",
                        fontSize: "13px",
                        height: "100% !important",
                        lineHeight: "1.5",
                      },

                      "& .MuiIconButton-root": {
                        backgroundColor: "white !important",
                        borderLeft: "none !important",
                      },
                    },
                  },
                }}
              />
            </div>
            <div>
              <p className="font-normal">End Date (or expected)</p>
              <BaseDatePicker
                className="border-none"
                type="date"
                id="endDate"
                name="endDate"
                value={parseDay(formik.values.endDate)}
                minDate={parseDay(formik.values.startDate)}
                onChange={(event) => {
                  const selectedDate = dayjs(event)
                    .utcOffset(0, true)
                    .format("YYYY-MM-DDTHH:mm:ss.SSS[Z]");
                  formik.setFieldValue("endDate", selectedDate); // Store as string
                }}
                onBlur={formik.handleBlur}
                error={formik.touched.endDate && Boolean(formik.errors.endDate)}
                helperText={formik.touched.endDate && formik.errors.endDate}
                slotProps={{
                  textField: {
                    sx: {
                      "& .MuiOutlinedInput-root": {
                        border: "none !important",
                        boxShadow: "1px 1px 4px 1px #00000021",
                        borderRadius: "5px",
                        backgroundColor: "white",
                        height: "48px",
                        display: "flex",
                        alignItems: "center",
                        margin: "8px 1.5px",
                      },
                      "& .MuiOutlinedInput-notchedOutline": {
                        border: "none !important",
                      },
                      "& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline":
                        {
                          border: "none !important",
                          boxShadow: "none",
                        },
                      "& .MuiInputBase-input": {
                        padding: "10px 14px",
                        fontSize: "13px",
                        height: "100% !important",
                        lineHeight: "1.5",
                      },

                      "& .MuiIconButton-root": {
                        backgroundColor: "white !important",
                        borderLeft: "none !important",
                      },
                    },
                  },
                }}
              />
            </div>
          </div>
        </div>
      </div>
      <div
        className="d-flex content-center  bg-white py-6 "
        style={{
          boxShadow: "0px 0px 14px 0px rgba(0, 0, 0, 0.07)",
        }}
      >
        <BaseButton
          className="px-[28px] py-[5px] bg-light text-primary hover:!text-primary"
          variant="outlined"
          color="primary"
          style={{
            marginRight: "16px",
          }}
          onClick={() => {
            close(false);
            setSelectedUserEducation(null);
          }}
        >
          Cancel
        </BaseButton>

        <BaseButton
          isLoading={isCreateEducationApiLoading}
          loaderSize={24}
          loaderColor="primary"
          className="px-[12px] py-[5px] min-w-32"
          color="primary"
          onClick={formik.handleSubmit}
          // Disabled by default; enabled only when there are valid unsaved changes.
          disabled={!formik.dirty || !formik.isValid || isCreateEducationApiLoading}
        >
          {isEdit ? "Update" : "Save Changes"}
        </BaseButton>
      </div>
    </BaseModal>
  );
};

export default AddEducationModal;
